class RackJudgeReviews {
  constructor(root) {
    this.root = root;
    this.shop = root.dataset.shopDomain;
    this.token = root.dataset.publicToken;
    this.type = root.dataset.reviewType || 'shop-reviews';
    this.pageSize = Math.max(3, Math.min(20, Number(root.dataset.pageSize) || 6));
    this.reviews = [];
    this.visibleCount = this.pageSize;
    this.ratingFilter = 0;
    this.query = '';
    this.sort = 'newest';
    this.summary = root.querySelector('[data-rack-judge-summary]');
    this.tools = root.querySelector('[data-rack-judge-tools]');
    this.filters = root.querySelector('[data-rack-judge-filters]');
    this.status = root.querySelector('[data-rack-judge-status]');
    this.list = root.querySelector('[data-rack-judge-list]');
    this.more = root.querySelector('[data-rack-judge-more]');
  }

  async start() {
    if (!this.token || !this.shop) {
      this.setStatus(this.root.dataset.emptyText);
      return;
    }
    try {
      const seen = new Set();
      for (let page = 1; page <= 200; page += 1) {
        const html = await this.fetchPage(page);
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const nodes = [...documentFragment.querySelectorAll('.jdgm-rev')];
        if (nodes.length === 0) break;
        let added = 0;
        nodes.forEach((node) => {
          const review = this.parseReview(node);
          if (!review || seen.has(review.id)) return;
          seen.add(review.id);
          this.reviews.push(review);
          added += 1;
        });
        if (added === 0) break;
      }
      if (this.reviews.length === 0) {
        this.setStatus(this.root.dataset.emptyText);
        return;
      }
      this.bind();
      this.render();
    } catch (_error) {
      this.setStatus(this.root.dataset.errorText);
    }
  }

  async fetchPage(page) {
    const endpoint = new URL('https://api.judge.me/api/v1/widgets/all_reviews_page');
    endpoint.searchParams.set('shop_domain', this.shop);
    endpoint.searchParams.set('api_token', this.token);
    endpoint.searchParams.set('review_type', this.type);
    endpoint.searchParams.set('page', String(page));
    const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Judge.me returned ${response.status}`);
    const responseText = await response.text();
    let payload;
    try {
      payload = JSON.parse(responseText);
    } catch (_error) {
      payload = responseText;
    }
    if (typeof payload === 'string') return payload;
    const body = [payload.all_reviews, payload.all_reviews_page, payload.html, payload.widget]
      .find((value) => typeof value === 'string' && value.trim()) || '';
    const header = typeof payload.all_reviews_header === 'string' ? payload.all_reviews_header : '';
    if (!body && page === 1) throw new Error('Judge.me returned no reviews markup');
    return `${header}${body}`;
  }

  parseReview(node) {
    const rating = Number(node.querySelector('.jdgm-rev__rating')?.dataset.score || 0);
    const body = node.querySelector('.jdgm-rev__body')?.textContent.trim() || '';
    if (!body || rating < 1 || rating > 5) return null;
    const author = node.querySelector('.jdgm-rev__author')?.textContent.trim() || 'Anonymous';
    const title = node.querySelector('.jdgm-rev__title')?.textContent.trim() || '';
    const dateNode = node.querySelector('time, .jdgm-rev__timestamp');
    const rawDate = dateNode?.getAttribute('datetime') || dateNode?.dataset.content || dateNode?.textContent.trim() || '';
    const parsedDate = new Date(rawDate);
    const date = Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().slice(0, 10);
    const product = node.querySelector('.jdgm-rev__prod-link, .jdgm-rev__product-link')?.textContent.trim() || '';
    const photoLinks = [...node.querySelectorAll('.jdgm-rev__pic-link')];
    const photoUrls = photoLinks.map((link) =>
      link.getAttribute('data-mfp-src') || link.getAttribute('href') || link.querySelector('img')?.getAttribute('src'));
    if (!photoUrls.length) {
      node.querySelectorAll('.jdgm-rev__pic-img').forEach((image) => photoUrls.push(image.getAttribute('data-src') || image.getAttribute('src')));
    }
    const photos = [...new Set(photoUrls.map((url) => this.safeImageUrl(url)).filter(Boolean))];
    const id = node.dataset.reviewId || `${author}|${date}|${title}|${body}`;
    return {
      id, author, title, body, date, product, photos, rating,
      verified: node.dataset.verifiedBuyer === 'true' || Boolean(node.querySelector('.jdgm-rev__buyer-badge')),
    };
  }

  safeImageUrl(rawUrl) {
    if (!rawUrl) return '';
    try {
      const url = new URL(rawUrl, window.location.origin);
      return url.protocol === 'https:' ? url.href : '';
    } catch (_error) {
      return '';
    }
  }

  bind() {
    this.root.querySelector('[data-rack-judge-search]').addEventListener('input', (event) => {
      this.query = event.target.value.trim().toLocaleLowerCase();
      this.visibleCount = this.pageSize;
      this.renderList();
    });
    this.root.querySelector('[data-rack-judge-sort]').addEventListener('change', (event) => {
      this.sort = event.target.value;
      this.visibleCount = this.pageSize;
      this.renderList();
    });
    this.filters.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rack-judge-rating]');
      if (!button) return;
      this.ratingFilter = Number(button.dataset.rackJudgeRating);
      this.visibleCount = this.pageSize;
      this.renderFilters();
      this.renderList();
    });
    this.more.addEventListener('click', () => {
      this.visibleCount += this.pageSize;
      this.renderList();
    });
  }

  render() {
    this.setStatus('');
    this.renderSummary();
    this.renderFilters();
    this.tools.hidden = false;
    this.renderList();
  }

  renderSummary() {
    const count = this.reviews.length;
    const average = this.reviews.reduce((sum, review) => sum + review.rating, 0) / count;
    const lead = this.make('div', 'rack-judge__score');
    const number = this.make('strong', '', average.toFixed(1));
    const meta = this.make('div');
    meta.append(this.make('span', 'rack-judge__stars', this.stars(average)));
    meta.append(this.make('small', '', `${count} ${this.root.dataset.countLabel}`));
    lead.append(number, meta);
    const bars = this.make('div', 'rack-judge__bars');
    for (let rating = 5; rating >= 1; rating -= 1) {
      const ratingCount = this.reviews.filter((review) => Math.round(review.rating) === rating).length;
      const row = this.make('div', 'rack-judge__bar');
      row.append(this.make('span', '', `${rating} ★`));
      const track = this.make('span', 'rack-judge__bar-track');
      const fill = this.make('span', 'rack-judge__bar-fill');
      fill.style.width = `${ratingCount / count * 100}%`;
      track.append(fill);
      row.append(track, this.make('span', '', `${ratingCount}`));
      bars.append(row);
    }
    this.summary.replaceChildren(lead, bars);
    this.summary.hidden = false;
  }

  renderFilters() {
    const allRatings = [0, 5, 4, 3, 2, 1];
    const buttons = allRatings.map((rating) => {
      const label = rating ? `${rating} ★` : this.root.dataset.allLabel;
      const button = this.make('button', 'rack-judge__filter', label);
      button.type = 'button';
      button.dataset.rackJudgeRating = String(rating);
      button.setAttribute('aria-pressed', String(rating === this.ratingFilter));
      return button;
    });
    this.filters.replaceChildren(...buttons);
    this.filters.hidden = false;
  }

  renderList() {
    let reviews = this.reviews.filter((review) => {
      const matchesRating = !this.ratingFilter || Math.round(review.rating) === this.ratingFilter;
      const text = `${review.author} ${review.title} ${review.body} ${review.product}`.toLocaleLowerCase();
      return matchesRating && (!this.query || text.includes(this.query));
    });
    reviews = reviews.sort((a, b) => {
      if (this.sort === 'highest') return b.rating - a.rating || b.date.localeCompare(a.date);
      if (this.sort === 'lowest') return a.rating - b.rating || b.date.localeCompare(a.date);
      return b.date.localeCompare(a.date);
    });
    const visible = reviews.slice(0, this.visibleCount);
    this.list.replaceChildren(...visible.map((review) => this.renderReview(review)));
    this.more.hidden = visible.length >= reviews.length;
    this.setStatus(reviews.length ? '' : this.root.dataset.noMatchText);
  }

  renderReview(review) {
    const item = this.make('article', 'rack-judge__review');
    const avatar = this.make('span', 'rack-judge__avatar', review.author.charAt(0).toUpperCase());
    avatar.setAttribute('aria-hidden', 'true');
    const content = this.make('div', 'rack-judge__review-main');
    const heading = this.make('div', 'rack-judge__review-heading');
    const author = this.make('strong', '', review.author);
    const stars = this.make('span', 'rack-judge__stars', this.stars(review.rating));
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
    heading.append(author, stars);
    if (review.verified) heading.append(this.make('span', 'rack-judge__verified', this.root.dataset.verifiedLabel));
    if (review.title) content.append(this.make('h3', '', review.title));
    content.prepend(heading);
    content.append(this.make('p', 'rack-judge__body', review.body));
    if (review.product && this.type === 'product-reviews') content.append(this.make('small', 'rack-judge__product', review.product));
    if (review.photos.length) {
      const gallery = this.make('div', 'rack-judge__photos');
      review.photos.forEach((src) => {
        const link = document.createElement('a');
        link.href = src;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        const image = document.createElement('img');
        image.src = src;
        image.alt = review.title || review.body.slice(0, 80);
        image.loading = 'lazy';
        gallery.append(link);
        link.append(image);
      });
      content.append(gallery);
    }
    const date = this.make('time', 'rack-judge__date', review.date);
    if (review.date) date.dateTime = review.date;
    item.append(avatar, content, date);
    return item;
  }

  stars(rating) {
    return `${'★'.repeat(Math.round(rating))}${'☆'.repeat(5 - Math.round(rating))}`;
  }

  make(tag, className = '', value = '') {
    const element = document.createElement(tag);
    if (className) element.className = className;
    element.textContent = value;
    return element;
  }

  setStatus(value) {
    this.status.textContent = value || '';
    this.status.hidden = !value;
  }
}

function initializeRackJudgeReviews(container = document) {
  const roots = container.matches?.('[data-rack-judge]') ? [container] : [...container.querySelectorAll('[data-rack-judge]')];
  roots.forEach((root) => {
    if (root.dataset.rackJudgeInitialized) return;
    root.dataset.rackJudgeInitialized = 'true';
    new RackJudgeReviews(root).start();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeRackJudgeReviews());
} else {
  initializeRackJudgeReviews();
}
document.addEventListener('shopify:section:load', (event) => initializeRackJudgeReviews(event.target));
